import { useState, useMemo, useCallback } from 'react'
import type { Worker, TimesheetEntry } from '../types'
import type { 
  DailyTimesheet,
  TimesheetHistoryItem, 
  TimesheetHistoryResponse,
  TimesheetDraftSaveRequest,
  TimesheetDraftSaveResponse,
  CopyWorkersRequest,
  CopyWorkersResponse
} from '../contracts/timesheet'
import { calculateHoursWorked } from './timesheet-business-rules.js'
import { getToken } from './api'

// Custom hook for timesheet state management
export const useTimesheetState = () => {
  const [timesheet, setTimesheet] = useState<DailyTimesheet | null>(null)
  const [workers, setWorkers] = useState<Worker[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Derived state - no need for complex state management
  const totalHours = useMemo(() => 
    timesheet?.entries.reduce((sum, entry) => sum + entry.hoursWorked, 0) || 0
  , [timesheet?.entries])
  
  const totalWorkers = timesheet?.entries.length || 0
  
  // Simple helper functions for common operations
  const addWorkerToTimesheet = (worker: Worker) => {
    if (!timesheet) return
    
    const hoursWorked = calculateHoursWorked('05:00', '17:00') // Uses centralized contract function (includes lunch deduction)
    
    const newEntry: TimesheetEntry = {
      id: Date.now(), // Temporary ID for new entries
      timesheetId: timesheet.id,
      workerId: worker.id,
      worker,
      timeIn: '05:00',
      timeOut: '17:00',
      hoursWorked,
      createdAt: new Date().toISOString()
    }
    
    setTimesheet(prev => prev ? {
      ...prev,
      entries: [...prev.entries, newEntry],
      totalWorkers: prev.entries.length + 1,
      totalHours: prev.totalHours + hoursWorked
    } : null)
  }

  const removeWorkerFromTimesheet = (workerId: number) => {
    if (!timesheet) return

    setTimesheet(prev => prev ? {
      ...prev,
      entries: prev.entries.filter(entry => entry.workerId !== workerId),
      totalWorkers: prev.entries.filter(entry => entry.workerId !== workerId).length,
      totalHours: prev.entries
        .filter(entry => entry.workerId !== workerId)
        .reduce((sum, entry) => sum + entry.hoursWorked, 0)
    } : null)
  }

  const updateWorkerTime = (workerId: number, timeIn: string, timeOut: string) => {
    if (!timesheet) return

    const hoursWorked = calculateHoursWorked(timeIn, timeOut)

    setTimesheet(prev => prev ? {
      ...prev,
      entries: prev.entries.map(entry => 
        entry.workerId === workerId 
          ? { ...entry, timeIn, timeOut, hoursWorked }
          : entry
      ),
      totalHours: prev.entries
        .map(entry => 
          entry.workerId === workerId 
            ? { ...entry, hoursWorked }
            : entry
        )
        .reduce((sum, entry) => sum + entry.hoursWorked, 0)
    } : null)
  }
  
  return { 
    timesheet, 
    setTimesheet, 
    workers, 
    setWorkers, 
    loading, 
    setLoading, 
    error, 
    setError, 
    totalHours, 
    totalWorkers, 
    addWorkerToTimesheet,
    removeWorkerFromTimesheet,
    updateWorkerTime
  }
}

// Enhanced timesheet state hook with draft and history management
export const useTimesheetHistoryState = (initialDate?: string) => {
  const [currentDate, setCurrentDate] = useState(initialDate || getTodayDate())
  const [historyData, setHistoryData] = useState<TimesheetHistoryItem[]>([])
  const [historyCollapsed, setHistoryCollapsed] = useState(false)
  const [draftLoaded, setDraftLoaded] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  
  // Use existing timesheet state hook
  const timesheetState = useTimesheetState()
  
  // Extract stable functions to avoid dependency issues
  const { setError, setLoading, setTimesheet } = timesheetState
  
  // ✅ CLEAN ARCHITECTURE: Always use direct API calls with proper proxy routing
  const loadHistoryData = useCallback(async (days: number = 7) => {
    try {
      const token = getToken()
      if (!token) throw new Error('Authentication required')
      
      // Use direct fetch which will be proxied correctly in development (port 3001)
      // and work with Vercel functions in production
      const response = await fetch(`/api/timesheet-history?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to load history: ${response.statusText}`)
      }
      
      const data: TimesheetHistoryResponse = await response.json()
      setHistoryData(data.timesheets)
      setError(null)
    } catch (error) {
      console.error('Failed to load history:', error)
      setError(error instanceof Error ? error.message : 'Failed to load history')
    }
  }, [setError])
  
  const switchToDate = useCallback(async (date: string) => {
    try {
      setLoading(true)
      const token = getToken()
      if (!token) throw new Error('Authentication required')
      
      // ✅ CLEAN ARCHITECTURE: Use direct API call with proxy routing
      const response = await fetch(`/api/timesheet-by-date?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (!response.ok) {
        throw new Error(`Failed to load timesheet: ${response.statusText}`)
      }
      
      const data: DailyTimesheet = await response.json()
      setCurrentDate(date)
      setTimesheet(data)
      setDraftLoaded(data.isDraft ?? false)
      setError(null)
    } catch (error) {
      console.error('Failed to switch date:', error)
      setError(error instanceof Error ? error.message : 'Failed to load timesheet for date')
    } finally {
      setLoading(false)
    }
  }, [setLoading, setTimesheet, setError])
  
  const copyWorkersFromDate = useCallback(async (fromDate: string) => {
    try {
      const token = getToken()
      if (!token) throw new Error('Authentication required')
      
      const request: CopyWorkersRequest = {
        fromDate,
        toDate: currentDate
      }
      
      // ✅ CLEAN ARCHITECTURE: Use direct API call with proxy routing
      const response = await fetch('/api/timesheet-copy-workers', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(request)
      })
      
      if (!response.ok) {
        throw new Error(`Failed to copy workers: ${response.statusText}`)
      }
      
      const data: CopyWorkersResponse = await response.json()
      
      if (data.success) {
        // Refresh current timesheet to show copied workers
        await switchToDate(currentDate)
        // Refresh history to show updated status
        await loadHistoryData()
      } else {
        setError(data.message || 'Failed to copy workers')
      }
    } catch (error) {
      console.error('Failed to copy workers:', error)
      setError(error instanceof Error ? error.message : 'Failed to copy workers')
    }
  }, [currentDate, switchToDate, loadHistoryData, setError])
  
  const saveDraftManually = useCallback(async () => {
    if (!timesheetState.timesheet) return
    
    setSavingDraft(true)
    try {
      const token = getToken()
      if (!token) throw new Error('Authentication required')
      
      const request: TimesheetDraftSaveRequest = {
        date: timesheetState.timesheet.date,
        entries: timesheetState.timesheet.entries.map(entry => ({
          workerId: entry.workerId,
          timeIn: entry.timeIn,
          timeOut: entry.timeOut
        })),
        payPeriodId: timesheetState.timesheet.payPeriodId,
        jobSiteId: timesheetState.timesheet.jobSiteId
      }
      
      // ✅ CLEAN ARCHITECTURE: Use direct API call with proxy routing
      const response = await fetch('/api/timesheet-draft-save', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(request)
      })
      
      if (!response.ok) {
        throw new Error(`Failed to save draft: ${response.statusText}`)
      }
      
      const data: TimesheetDraftSaveResponse = await response.json()
      setTimesheet(data.timesheet)
      await loadHistoryData() // Refresh history to show updated save time
      setError(null)
    } catch (error) {
      console.error('Failed to save draft:', error)
      setError(error instanceof Error ? error.message : 'Failed to save draft')
    } finally {
      setSavingDraft(false)
    }
  }, [timesheetState.timesheet, setTimesheet, loadHistoryData, setError])
  
  const loadTodaysDraft = useCallback(async () => {
    const today = getTodayDate()
    if (currentDate === today) {
      await switchToDate(today)
    }
  }, [currentDate, switchToDate])
  
  return {
    ...timesheetState,
    currentDate,
    setCurrentDate,
    historyData,
    historyCollapsed,
    setHistoryCollapsed,
    draftLoaded,
    setDraftLoaded,
    savingDraft,
    loadHistoryData,
    switchToDate,
    copyWorkersFromDate,
    saveDraftManually,
    loadTodaysDraft
  }
}

// Helper function for date formatting
const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0]
}

// Note: Hours calculation is now centralized in src/contracts/timesheet.ts
// Use calculateHoursWorked() from the contracts for consistent lunch deduction logic

// Normalize worker name for duplicate detection
export const normalizeWorkerName = (name: string): string => {
  return name.toLowerCase().trim().replace(/\s+/g, ' ')
}

// Simple form validation
export const validateTimesheet = (timesheet: DailyTimesheet): string[] => {
  const errors: string[] = []

  if (!timesheet.date) {
    errors.push('Date is required')
  }

  if (timesheet.entries.length === 0) {
    errors.push('At least one worker is required')
  }

  timesheet.entries.forEach((entry, index) => {
    if (entry.hoursWorked <= 0) {
      errors.push(`Worker ${entry.worker?.name || index + 1} has invalid hours`)
    }
    if (entry.hoursWorked > 24) {
      errors.push(`Worker ${entry.worker?.name || index + 1} has more than 24 hours`)
    }
  })

  return errors
}

// Check for potential duplicate workers
export const findPotentialDuplicates = (newWorkerName: string, existingWorkers: Worker[]): Worker[] => {
  const normalized = normalizeWorkerName(newWorkerName)
  
  return existingWorkers.filter(worker => {
    const similarity = calculateSimilarity(normalized, worker.normalizedName)
    return similarity > 0.8 // 80% similarity threshold
  })
}

// Simple string similarity calculation
const calculateSimilarity = (str1: string, str2: string): number => {
  const longer = str1.length > str2.length ? str1 : str2
  const shorter = str1.length > str2.length ? str2 : str1
  
  if (longer.length === 0) return 1.0
  
  const distance = levenshteinDistance(longer, shorter)
  return (longer.length - distance) / longer.length
}

// Levenshtein distance implementation
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = []
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i]
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1]
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        )
      }
    }
  }
  
  return matrix[str2.length][str1.length]
} 